import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, timer, NEVER, of } from 'rxjs';
import {
  switchMap,
  tap,
  catchError,
  takeUntil,
  filter,
  distinctUntilChanged
} from 'rxjs/operators';
import { ConnectionStatus, ConnectionConfig } from '../models';
import { NetworkQualityService } from './network-quality.service';
import { CircuitBreakerService } from './circuit-breaker.service';
import { WebSocketConnectionService } from './websocket-connection.service';

/**
 * Reconnection strategy and management service
 * Handles intelligent reconnection logic with network awareness
 */
@Injectable({
  providedIn: 'root'
})
export class ReconnectionService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private activeTimeouts: Set<number> = new Set();
  private config: ConnectionConfig = {
    maxReconnectAttempts: 10,
    baseReconnectDelay: 1000,
    maxReconnectDelay: 20000,    // Reduced from 30000ms for faster recovery
    backoffMultiplier: 1.3,      // Reduced from 1.5 for less aggressive backoff
    pingInterval: 15000,         // Reduced from 30000ms for faster detection
    connectionTimeout: 7000,     // Reduced from 10000ms for quicker timeouts
    enableCircuitBreaker: true
  };

  private currentAttempts = 0;
  private targetUrl: string | null = null;
  private isReconnecting = false;
  private lastConnectionTime: number = 0;
  private failureHistory: Array<{ timestamp: number; reason: string }> = [];
  private hibernationDetected = false;
  private lastReconnectionAttempt: number = 0;
  private reconnectionDebounceTime: number = 2000; // 2 seconds minimum between attempts

  // Enhanced ping monitoring
  private pingResponseTimeouts: number = 0;
  private consecutivePingFailures: number = 0;
  private lastSuccessfulPingTime: number = 0;
  private connectionChurnDetected: boolean = false;

  constructor(
    private connectionService: WebSocketConnectionService,
    private networkService: NetworkQualityService,
    private circuitBreaker: CircuitBreakerService
  ) {
    this.setupAutoReconnect();
    this.setupHibernationDetection();
    this.setupPingMonitoring();
  }

  /**
   * Update reconnection configuration
   */
  updateConfig(config: Partial<ConnectionConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Start managed connection with automatic reconnection
   */
  connect(url: string): Observable<boolean> {
    this.targetUrl = url;
    this.currentAttempts = 0;
    this.isReconnecting = false;

    return this.attemptConnection();
  }

  /**
   * Force a reconnection attempt
   */
  forceReconnect(): Observable<boolean> {
    if (!this.targetUrl) {
      return NEVER;
    }

    this.currentAttempts = 0;
    return this.attemptConnection();
  }

  /**
   * Stop all reconnection attempts
   */
  stopReconnection(): void {
    this.isReconnecting = false;
    this.targetUrl = null;
    this.currentAttempts = 0;

    // Clear all active timeouts
    this.activeTimeouts.forEach(timeoutId => window.clearTimeout(timeoutId));
    this.activeTimeouts.clear();
  }

  /**
   * Get current reconnection status
   */
  getReconnectionInfo() {
    return {
      isReconnecting: this.isReconnecting,
      attempts: this.currentAttempts,
      maxAttempts: this.config.maxReconnectAttempts,
      targetUrl: this.targetUrl,
      nextDelay: this.calculateDelay(this.currentAttempts)
    };
  }

  private setupAutoReconnect(): void {
    // Auto-reconnect on disconnection with enhanced WiFi change detection
    this.connectionService.status$
      .pipe(
        filter(status => status === ConnectionStatus.DISCONNECTED || status === ConnectionStatus.FAILED),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.targetUrl && !this.isReconnecting) {
          // Detect if this was likely a WiFi change or hibernation
          this.detectDisconnectionCause().then(cause => {
            console.log(`[ReconnectionService] Disconnection cause detected: ${cause}`);
            this.handleDisconnectionByCause(cause);
          });
        }
      });

    // Enhanced network quality monitoring with WiFi change detection
    this.networkService.networkQuality$
      .pipe(
        filter(quality => quality === 'good' || quality === 'excellent'),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.targetUrl &&
            !this.connectionService.isConnected() &&
            !this.isReconnecting) {

          // Additional check for recent WiFi changes
          this.networkService.isSuitableForWebSocketWithDelay().subscribe(isSuitable => {
            if (isSuitable) {
              console.log('[ReconnectionService] Network improved and stable, initiating reconnection');
              this.startReconnectionSequence();
            }
          });
        }
      });

    // Monitor for WiFi/network interface changes (enhanced detection)
    this.setupWiFiChangeDetection();
  }

  /**
   * Setup WiFi change detection using multiple methods
   */
  private setupWiFiChangeDetection(): void {
    // Method 1: Connection type changes (if supported)
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      if (connection) {
        const handleConnectionChange = () => {
          console.log('[ReconnectionService] Network connection change detected');
          this.handleNetworkChange();
        };

        connection.addEventListener('change', handleConnectionChange);

        // Cleanup on destroy
        this.destroy$.subscribe(() => {
          connection.removeEventListener('change', handleConnectionChange);
        });
      }
    }

    // Method 2: Rapid online/offline cycles (indicates WiFi switching)
    let onlineChangeCount = 0;
    let lastOnlineChange = 0;

    const resetCounter = () => {
      onlineChangeCount = 0;
    };

    this.networkService.visibilityState$
      .pipe(
        filter(state => state.isOnline !== undefined),
        takeUntil(this.destroy$)
      )
      .subscribe(state => {
        const now = Date.now();
        if (now - lastOnlineChange < 5000) { // 5 seconds
          onlineChangeCount++;
          if (onlineChangeCount >= 2) {
            console.log('[ReconnectionService] Rapid network changes detected (likely WiFi switch)');
            this.handleNetworkChange();
            resetCounter();
          }
        } else {
          onlineChangeCount = 1;
        }
        lastOnlineChange = now;

        // Reset counter after 10 seconds
        setTimeout(resetCounter, 10000);
      });
  }

  /**
   * Handle network changes with appropriate delays and validation
   */
  private handleNetworkChange(): void {
    if (!this.targetUrl || this.connectionService.isConnected()) {
      return;
    }

    // More conservative approach: only trigger if we're actually disconnected for a bit
    const timeSinceDisconnection = Date.now() - this.lastConnectionTime;
    if (timeSinceDisconnection < 5000) {
      console.log('[ReconnectionService] Network change detected but recent disconnection, waiting...');
      return;
    }

    // Wait for network to stabilize after change
    setTimeout(() => {
      this.networkService.isSuitableForWebSocketWithDelay().subscribe(isSuitable => {
        if (isSuitable && !this.isReconnecting) {
          console.log('[ReconnectionService] Network change stabilized, attempting reconnection');
          this.currentAttempts = Math.max(0, this.currentAttempts - 2); // Reduce penalty for network changes
          this.startReconnectionSequence();
        }
      });
    }, 3000); // 3-second stabilization delay
  }

  /**
   * Detect the likely cause of disconnection for appropriate handling
   */
  private async detectDisconnectionCause(): Promise<'normal' | 'hibernation' | 'wifi_change' | 'network_issue' | 'application_navigation'> {
    const timeSinceLastConnection = Date.now() - this.lastConnectionTime;
    const recentFailures = this.failureHistory.slice(-3);

    // Check for application navigation scenario (short disconnections during normal use)
    const isShortDisconnection = timeSinceLastConnection < 10000; // Less than 10 seconds
    const hasRecentActivity = this.hasRecentApplicationActivity();

    if (isShortDisconnection && hasRecentActivity) {
      return 'application_navigation';
    }

    // True hibernation: Long gap in activity AND page was hidden/inactive
    const isLongGap = timeSinceLastConnection > 60000; // 1 minute
    const wasPageHidden = this.wasPageHiddenDuringDisconnection();

    if (isLongGap && wasPageHidden) {
      return 'hibernation';
    }

    // If long gap but page was visible, likely network issue, not hibernation
    if (isLongGap && !wasPageHidden) {
      return 'network_issue';
    }

    // WiFi change: Rapid network state changes or specific error patterns
    const hasAbnormalClosures = recentFailures.some(f => f.reason.includes('1006'));
    const hasRapidChanges = recentFailures.length >= 2 &&
      recentFailures[recentFailures.length - 1].timestamp - recentFailures[0].timestamp < 10000;

    if (hasAbnormalClosures && hasRapidChanges) {
      return 'wifi_change';
    }

    // Network issue: Multiple failures in sequence
    if (recentFailures.length >= 2) {
      return 'network_issue';
    }

    return 'normal';
  }    /**
   * Handle disconnection based on detected cause
   */
  private handleDisconnectionByCause(cause: string): void {
    switch (cause) {
      case 'hibernation':
        // Longer delay, reset attempts, wait for stability
        console.log('[ReconnectionService] True hibernation detected, using extended recovery');
        this.currentAttempts = 0;
        setTimeout(() => this.startReconnectionSequence(), 5000);
        break;

      case 'application_navigation':
        // Quick reconnection for normal app navigation
        console.log('[ReconnectionService] Application navigation detected, quick reconnection');
        this.currentAttempts = Math.max(0, this.currentAttempts - 2); // Reduce penalty
        setTimeout(() => this.startReconnectionSequence(), 1000);
        break;

      case 'wifi_change':
        // Medium delay, minimal penalty, ensure network stability
        console.log('[ReconnectionService] WiFi change detected, stabilizing network');
        this.currentAttempts = Math.max(0, this.currentAttempts - 1);
        setTimeout(() => this.startReconnectionSequence(), 3000);
        break;

      case 'network_issue':
        // Standard reconnection with current attempt count
        console.log('[ReconnectionService] Network issue detected, standard reconnection');
        setTimeout(() => this.startReconnectionSequence(), 2000);
        break;

      default:
        // Normal disconnection
        console.log('[ReconnectionService] Normal disconnection, immediate reconnection');
        this.startReconnectionSequence();
        break;
    }
  }

  private startReconnectionSequence(): void {
    if (this.currentAttempts >= this.config.maxReconnectAttempts) {
      console.log('[ReconnectionService] Max reconnection attempts reached');
      return;
    }

    if (this.config.enableCircuitBreaker && !this.circuitBreaker.canExecute()) {
      console.log('[ReconnectionService] Circuit breaker is open, blocking reconnection');
      return;
    }

    // CONNECTION GUARD: Check if a connection attempt is already in progress
    if (this.connectionService.isConnectionInProgressCheck && this.connectionService.isConnectionInProgressCheck()) {
      console.log('[ReconnectionService] Connection already in progress, skipping reconnection attempt');
      return;
    }

    // CONNECTION GUARD: Check if already connected or connecting
    if (this.connectionService.isConnected() || this.connectionService.isConnecting()) {
      console.log('[ReconnectionService] Already connected or connecting, skipping reconnection attempt:', {
        isConnected: this.connectionService.isConnected(),
        isConnecting: this.connectionService.isConnecting(),
        status: this.connectionService.getStatus()
      });
      return;
    }

    // Additional check for connection churn
    if (this.connectionChurnDetected && this.currentAttempts > 1) {
      console.log('[ReconnectionService] Connection churn detected, extending delay');
      const extendedDelay = this.reconnectionDebounceTime * 2;
      const timeoutId = window.setTimeout(() => this.startReconnectionSequence(), extendedDelay);
      this.activeTimeouts.add(timeoutId);
      return;
    }

    // Debounce rapid reconnection attempts
    const timeSinceLastAttempt = Date.now() - this.lastReconnectionAttempt;
    if (timeSinceLastAttempt < this.reconnectionDebounceTime) {
      console.log(`[ReconnectionService] Debouncing reconnection attempt (${timeSinceLastAttempt}ms < ${this.reconnectionDebounceTime}ms)`);
      const remainingDebounce = this.reconnectionDebounceTime - timeSinceLastAttempt;
      const timeoutId = window.setTimeout(() => this.startReconnectionSequence(), remainingDebounce);
      this.activeTimeouts.add(timeoutId);
      return;
    }

    this.lastReconnectionAttempt = Date.now();
    this.isReconnecting = true;
    const delay = this.calculateDelay(this.currentAttempts);

    console.log(`[ReconnectionService] Starting reconnection sequence (attempt ${this.currentAttempts + 1}/${this.config.maxReconnectAttempts})`);

    timer(delay)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.attemptConnection()),
        catchError(() => {
          this.currentAttempts++;
          this.isReconnecting = false;

          // Schedule next attempt if not at limit
          if (this.currentAttempts < this.config.maxReconnectAttempts) {
            console.log(`[ReconnectionService] Attempt failed, scheduling retry in 1 second`);
            const timeoutId = window.setTimeout(() => this.startReconnectionSequence(), 1000);
            this.activeTimeouts.add(timeoutId);
          } else {
            console.log('[ReconnectionService] All reconnection attempts exhausted');
          }

          return NEVER;
        })
      )
      .subscribe(success => {
        if (success) {
          this.currentAttempts = 0;
          this.isReconnecting = false;
        } else {
          this.currentAttempts++;
          this.isReconnecting = false;

          if (this.currentAttempts < this.config.maxReconnectAttempts) {
            const timeoutId = window.setTimeout(() => this.startReconnectionSequence(), 1000);
            this.activeTimeouts.add(timeoutId);
          }
        }
      });
  }

  private attemptConnection(): Observable<boolean> {
    if (!this.targetUrl) {
      return of(false);
    }

    // Enhanced network suitability check for hibernation scenarios
    return this.networkService.isSuitableForWebSocketWithDelay()
      .pipe(
        switchMap(isNetworkSuitable => {
          if (!isNetworkSuitable) {
            console.log('[ReconnectionService] Network not suitable for WebSocket, delaying...');
            return of(false);
          }

          // Check circuit breaker
          if (this.config.enableCircuitBreaker && !this.circuitBreaker.canExecute()) {
            console.log('[ReconnectionService] Circuit breaker is open, blocking connection');
            return of(false);
          }

          // Calculate timeout based on hibernation detection
          const adaptiveTimeout = this.calculateAdaptiveConnectionTimeout();

          console.log(`[ReconnectionService] Attempting connection with ${adaptiveTimeout}ms timeout`);

          return this.connectionService.connect(this.targetUrl!, adaptiveTimeout)
            .pipe(
              tap(success => {
                if (success) {
                  this.circuitBreaker.recordSuccess();
                  this.lastConnectionTime = Date.now();
                  console.log('[ReconnectionService] Connection successful');
                } else {
                  this.circuitBreaker.recordFailure();
                  this.recordFailure('connection_failed');
                  console.log('[ReconnectionService] Connection failed');
                }
              }),
              catchError(error => {
                this.circuitBreaker.recordFailure();

                // Record specific error types for pattern analysis
                const errorMessage = error instanceof Error ? error.message : String(error);
                if (errorMessage.includes('timeout')) {
                  this.recordFailure('timeout_error');
                } else if (errorMessage.includes('1006')) {
                  this.recordFailure('abnormal_closure_1006');
                } else {
                  this.recordFailure('general_error');
                }

                console.error('[ReconnectionService] Connection error:', errorMessage);
                throw error;
              })
            );
        })
      );
  }

  /**
   * Calculate adaptive connection timeout based on failure patterns
   */
  private calculateAdaptiveConnectionTimeout(): number {
    const timeSinceLastConnection = Date.now() - this.lastConnectionTime;
    const isLikelyHibernation = timeSinceLastConnection > 60000; // 1 minute

    const recentTimeouts = this.failureHistory.filter(
      failure => failure.timestamp > Date.now() - 60000 &&
                 failure.reason.includes('timeout')
    ).length;

    let timeout = this.config.connectionTimeout;

    // Hibernation scenario: much longer timeout
    if (isLikelyHibernation || this.hibernationDetected) {
      timeout = Math.max(timeout * 3, 20000); // At least 20 seconds
    }

    // Multiple recent timeouts: progressive increase
    if (recentTimeouts > 1) {
      timeout = Math.min(timeout * (1 + recentTimeouts * 0.5), 30000); // Max 30 seconds
    }

    return timeout;
  }

  private calculateDelay(attempt: number): number {
    // Detect hibernation scenario
    const timeSinceLastConnection = Date.now() - this.lastConnectionTime;
    const isLikelyHibernation = timeSinceLastConnection > 60000; // 1 minute gap

    // Check for abnormal closure patterns (1006 errors)
    const recentAbnormalClosures = this.failureHistory.filter(
      failure => failure.timestamp > Date.now() - 30000 &&
                 failure.reason.includes('1006')
    ).length;

    let baseDelay = this.config.baseReconnectDelay;

    // Hibernation recovery: longer initial delay to let network stabilize
    if (isLikelyHibernation || this.hibernationDetected) {
      baseDelay = Math.max(5000, baseDelay); // Minimum 5 seconds
      this.hibernationDetected = false; // Reset flag
    }

    // Multiple 1006 errors: progressive backoff
    if (recentAbnormalClosures > 2) {
      baseDelay = Math.max(baseDelay * (recentAbnormalClosures - 1), baseDelay);
    }

    // Standard exponential backoff
    const delay = baseDelay * Math.pow(this.config.backoffMultiplier, attempt);

    // Add jitter for hibernation scenarios (±25%)
    const jitter = isLikelyHibernation ? delay * 0.25 * (Math.random() - 0.5) : 0;

    return Math.min(delay + jitter, this.config.maxReconnectDelay);
  }

  /**
   * Setup hibernation detection based on page visibility and connection patterns
   */
  private setupHibernationDetection(): void {
    // Detect page visibility changes (hibernation/sleep)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        // Page became visible - might be waking from hibernation
        const timeSinceHidden = Date.now() - this.lastConnectionTime;
        if (timeSinceHidden > 30000) { // 30 seconds suggests hibernation
          this.hibernationDetected = true;
          this.handleHibernationRecovery();
        }
      }
    });

    // Monitor connection events for hibernation patterns and ping failures
    this.connectionService.events$
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        if (event.type === 'error' && event.data?.code === 1006) {
          this.recordFailure('abnormal_closure_1006');
        }

        if (event.type === 'status' && event.data?.code === 'connected') {
          this.lastConnectionTime = Date.now();
          this.clearOldFailures(); // Clean up old failure history
        }

        // Handle ping-related connection health events
        if (event.type === 'message' && event.data?.ping_max_failures_reached) {
          console.log('[ReconnectionService] Maximum ping failures detected, preparing for reconnection');
          this.handlePingFailureEvent(event.data);
        }

        if (event.type === 'message' && event.data?.connection_health_critical) {
          console.log('[ReconnectionService] Critical connection health detected');
          this.handleCriticalHealthEvent(event.data);
        }

        if (event.type === 'message' && event.data?.ping_emergency_low_rate) {
          console.log('[ReconnectionService] Emergency low response rate detected');
          this.handleEmergencyResponseRateEvent(event.data);
        }
      });
  }

  /**
   * Handle hibernation recovery with network stabilization wait
   */
  private handleHibernationRecovery(): void {
    if (!this.targetUrl || this.connectionService.isConnected()) {
      return;
    }

    console.log('[ReconnectionService] Hibernation detected, initiating recovery...');

    // Wait for network to stabilize after hibernation
    setTimeout(() => {
      if (!this.connectionService.isConnected() && this.targetUrl) {
        this.currentAttempts = 0; // Reset attempts for hibernation recovery
        console.log('[ReconnectionService] Starting hibernation recovery sequence');
        this.startReconnectionSequence();
      }
    }, 3000); // 3-second stabilization delay
  }

  /**
   * Record connection failure with timestamp and reason
   */
  private recordFailure(reason: string): void {
    this.failureHistory.push({
      timestamp: Date.now(),
      reason
    });

    // Keep only last 20 failures to prevent memory bloat
    if (this.failureHistory.length > 20) {
      this.failureHistory = this.failureHistory.slice(-20);
    }
  }

  /**
   * Get count of recent failures (last 2 minutes)
   */
  private getRecentFailureCount(): number {
    const twoMinutesAgo = Date.now() - (2 * 60 * 1000);
    return this.failureHistory.filter(f => f.timestamp > twoMinutesAgo).length;
  }

  /**
   * Clear failures older than 5 minutes
   */
  private clearOldFailures(): void {
    const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
    this.failureHistory = this.failureHistory.filter(
      failure => failure.timestamp > fiveMinutesAgo
    );
  }

  /**
   * Setup ping monitoring to detect connection issues
   */
  private setupPingMonitoring(): void {
    // Monitor ping responses and timeouts
    this.connectionService.events$
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        if (event.type === 'message' && event.data?.verification_timeout) {
          this.handlePingTimeout();
        } else if (event.type === 'message' && event.data?.verification_success) {
          this.handlePingSuccess();
        } else if (event.type === 'error' && event.data?.error?.includes('ping')) {
          this.handlePingFailure();
        }
      });

    // Monitor for connection churn (frequent reconnections)
    this.connectionService.status$
      .pipe(
        filter(status => status === ConnectionStatus.CONNECTED),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.checkForConnectionChurn();
      });
  }

    /**
   * Handle ping timeout events
   */
  private handlePingTimeout(): void {
    this.pingResponseTimeouts++;
    this.consecutivePingFailures++;

    console.warn('[ReconnectionService] Ping timeout detected:', {
      timeouts: this.pingResponseTimeouts,
      consecutiveFailures: this.consecutivePingFailures,
      lastSuccessfulPing: this.lastSuccessfulPingTime
    });

    // Note: The main consecutive failure handling is now in WebSocketConnectionService
    // This method provides additional monitoring and backup logic

    // If multiple ping failures with no recent successful pings, consider connection degraded
    if (this.consecutivePingFailures >= 3) {  // Reduced threshold for faster response
      const timeSinceLastSuccess = Date.now() - this.lastSuccessfulPingTime;

      console.warn('[ReconnectionService] Multiple consecutive ping failures detected:', {
        consecutiveFailures: this.consecutivePingFailures,
        timeSinceLastSuccess: `${timeSinceLastSuccess}ms`
      });

      // If no successful ping in 1 minute, treat as connection degradation
      if (timeSinceLastSuccess > 60000) {
        console.error('[ReconnectionService] No successful ping in 1 minute, treating as connection failure');
        this.circuitBreaker.recordFailure();
        this.recordFailure('extended_ping_timeout');
      }

      // Reset consecutive failures after recording to prevent spam
      this.consecutivePingFailures = 0;

      // If we're connected but pings are consistently failing, check connection health
      if (this.connectionService.isConnected() && this.targetUrl && !this.isReconnecting) {
        console.log('[ReconnectionService] Connected but persistent ping failures, scheduling health check');
        setTimeout(() => this.checkConnectionHealth(), 2000);  // Reduced delay
      }
    }
  }

  /**
   * Handle successful ping responses
   */
  private handlePingSuccess(): void {
    this.lastSuccessfulPingTime = Date.now();
    this.consecutivePingFailures = 0; // Reset failure counter
    this.connectionChurnDetected = false; // Reset churn detection

    console.log('[ReconnectionService] Ping success, connection health restored');
  }

  /**
   * Handle ping failure events
   */
  private handlePingFailure(): void {
    this.consecutivePingFailures++;
    this.recordFailure('ping_send_failure');

    console.warn('[ReconnectionService] Ping send failure:', {
      consecutiveFailures: this.consecutivePingFailures
    });
  }

  /**
   * Check for connection churn (too many reconnections in short time)
   */
  private checkForConnectionChurn(): void {
    const now = Date.now();
    const connectionDuration = now - this.lastConnectionTime;

    // If connection lasted less than 30 seconds and we have recent failures
    if (connectionDuration < 30000 && this.currentAttempts > 0) {
      this.connectionChurnDetected = true;

      console.warn('[ReconnectionService] Connection churn detected:', {
        duration: connectionDuration,
        attempts: this.currentAttempts,
        recentFailures: this.getRecentFailureCount()
      });

      // Increase debounce time for churning connections
      this.reconnectionDebounceTime = Math.min(10000, this.reconnectionDebounceTime * 1.5);

      // Record churn as a failure pattern
      this.recordFailure('connection_churn');
    } else if (connectionDuration > 60000) {
      // Reset churn detection for stable connections
      this.connectionChurnDetected = false;
      this.reconnectionDebounceTime = 2000; // Reset to default
    }

    this.lastConnectionTime = now;
  }

  /**
   * Check connection health when pings are failing but connection appears active
   */
  private checkConnectionHealth(): void {
    if (!this.connectionService.isConnected() || !this.targetUrl) {
      return;
    }

    const timeSinceLastPing = Date.now() - this.lastSuccessfulPingTime;
    const connectionAge = Date.now() - this.lastConnectionTime;

    console.log('[ReconnectionService] Checking connection health:', {
      timeSinceLastPing,
      connectionAge,
      pingTimeouts: this.pingResponseTimeouts,
      isChurning: this.connectionChurnDetected
    });

    // If no successful ping in 2 minutes, force reconnection
    if (timeSinceLastPing > 120000) {
      console.warn('[ReconnectionService] No successful ping in 2 minutes, forcing reconnection');
      this.forceHealthyReconnection();
    }
    // If connection is churning, use circuit breaker
    else if (this.connectionChurnDetected && this.pingResponseTimeouts > 5) {
      console.warn('[ReconnectionService] Connection churning with ping issues, opening circuit breaker');
      this.circuitBreaker.recordFailure();
    }
  }

  /**
   * Force a healthy reconnection when connection appears stale
   */
  private forceHealthyReconnection(): void {
    console.log('[ReconnectionService] Forcing healthy reconnection due to ping issues');

    // Disconnect current connection
    this.connectionService.disconnect();

    // Reset ping counters
    this.pingResponseTimeouts = 0;
    this.consecutivePingFailures = 0;
    this.connectionChurnDetected = false;

    // Reset attempts to avoid excessive backoff
    this.currentAttempts = Math.max(0, this.currentAttempts - 2);

    // Wait for network to stabilize before reconnecting
    setTimeout(() => {
      if (this.targetUrl && !this.isReconnecting) {
        console.log('[ReconnectionService] Starting healthy reconnection sequence');
        this.startReconnectionSequence();
      }
    }, 3000);
  }

  /**
   * Handle ping failure events that indicate connection issues
   */
  private handlePingFailureEvent(data: any): void {
    const consecutiveFailures = data.consecutiveFailures || 0;
    const responseRate = data.pingResponseRate || 0;

    console.log('[ReconnectionService] Ping failure event received:', {
      consecutiveFailures,
      responseRate: `${responseRate.toFixed(1)}%`,
      currentAttempts: this.currentAttempts,
      isReconnecting: this.isReconnecting,
      connectionStatus: this.connectionService.getStatus()
    });

    // Record ping failures for pattern analysis
    this.recordFailure(`ping_max_failures_${consecutiveFailures}`);

    // FIX: Check if connection is still actually established before triggering reconnection
    if (this.connectionService.isConnected()) {
      console.log('[ReconnectionService] Connection still active despite ping failures - allowing circuit breaker time to recover');

      // Give circuit breaker time to reset and retry pings before forcing reconnection
      setTimeout(() => {
        // Only trigger reconnection if still experiencing issues
        if (this.connectionService.isConnected() && !this.isReconnecting && this.targetUrl) {
          const currentPingStats = this.connectionService.getPingStats();

          if (currentPingStats.consecutiveFailures >= 2 && currentPingStats.responseRate < 70) {
            console.log('[ReconnectionService] Ping issues persist - triggering reconnection');
            this.currentAttempts = Math.max(0, this.currentAttempts - 1);
            this.startReconnectionSequence();
          } else {
            console.log('[ReconnectionService] Ping health improved - no reconnection needed');
          }
        }
      }, 5000); // 5 second grace period for circuit breaker recovery

      return;
    }

    // If not already reconnecting and we have a target URL, trigger reconnection
    if (!this.isReconnecting && this.targetUrl) {
      console.log('[ReconnectionService] Triggering reconnection due to ping failures');

      // Don't penalize heavily for ping failures - they might be temporary
      this.currentAttempts = Math.max(0, this.currentAttempts - 1);

      // Start reconnection with a short delay to allow cleanup
      setTimeout(() => {
        this.startReconnectionSequence();
      }, 2000);
    }
  }

  /**
   * Handle critical connection health events
   */
  private handleCriticalHealthEvent(data: any): void {
    const reason = data.reason || 'unknown';
    const failureCount = data.failureCount || 0;

    console.log('[ReconnectionService] Critical health event received:', {
      reason,
      failureCount,
      currentStatus: this.connectionService.getStatus(),
      isReconnecting: this.isReconnecting
    });

    // Record the critical health event
    this.recordFailure(`critical_health_${reason}`);

    // For ping-related critical health, treat as connection degradation
    if (reason === 'max_consecutive_ping_failures') {
      console.log('[ReconnectionService] Critical ping failure pattern detected');

      // If connection appears active but ping health is critical, force a health check
      if (this.connectionService.isConnected() && !this.isReconnecting) {
        console.log('[ReconnectionService] Connection active but unhealthy, evaluating reconnection');

        // Give a brief window for natural recovery before forcing reconnection
        setTimeout(() => {
          if (this.connectionService.isConnected() && this.targetUrl && !this.isReconnecting) {
            console.log('[ReconnectionService] Forcing reconnection due to persistent ping health issues');
            this.forceReconnect().subscribe();
          }
        }, 5000); // 5 second grace period
      }
    }
  }

  /**
   * Handle emergency response rate events - immediate action required
   */
  private handleEmergencyResponseRateEvent(data: any): void {
    const responseRate = data.responseRate || 0;
    const consecutiveFailures = data.consecutiveFailures || 0;
    const timeSinceLastResponse = data.timeSinceLastResponse || 0;

    console.error('[ReconnectionService] Emergency response rate event:', {
      responseRate: `${responseRate.toFixed(1)}%`,
      consecutiveFailures,
      timeSinceLastResponse: `${timeSinceLastResponse}ms`,
      currentStatus: this.connectionService.getStatus(),
      isReconnecting: this.isReconnecting
    });

    // Record emergency pattern
    this.recordFailure(`emergency_response_rate_${responseRate.toFixed(0)}`);

    // EMERGENCY: If not already reconnecting, trigger immediate reconnection
    if (!this.isReconnecting && this.targetUrl) {
      console.error('[ReconnectionService] EMERGENCY: Triggering immediate reconnection due to critical response rate');

      // Don't penalize heavily for emergency situations
      this.currentAttempts = Math.max(0, this.currentAttempts - 2);

      // Cancel any existing reconnection delays
      this.activeTimeouts.forEach(timeoutId => window.clearTimeout(timeoutId));
      this.activeTimeouts.clear();

      // Start emergency reconnection immediately
      setTimeout(() => {
        if (!this.isReconnecting && this.targetUrl) {
          console.log('[ReconnectionService] Starting emergency reconnection sequence');
          this.startReconnectionSequence();
        }
      }, 250); // 250ms emergency delay
    }
  }
  private hasRecentApplicationActivity(): boolean {
    // Check if network service shows the application is visible and active
    const networkState = this.networkService.getCurrentVisibilityState();
    const isApplicationVisible = networkState.isVisible && networkState.isFocused;

    // Check if there was recent activity (network checks, connections)
    const timeSinceLastCheck = Date.now() - networkState.lastCheck.getTime();
    const hasRecentNetworkActivity = timeSinceLastCheck < 30000; // 30 seconds

    return isApplicationVisible || hasRecentNetworkActivity;
  }

  /**
   * Check if the page was hidden during the disconnection period
   */
  private wasPageHiddenDuringDisconnection(): boolean {
    // Check current visibility state
    const networkState = this.networkService.getCurrentVisibilityState();

    // If page is currently hidden, likely was hidden during disconnection
    if (!networkState.isVisible) {
      return true;
    }

    // Check if the page recently became visible (suggesting it was hidden)
    const timeSinceLastCheck = Date.now() - networkState.lastCheck.getTime();
    const recentlyBecameVisible = timeSinceLastCheck < 5000; // 5 seconds

    // If recently became visible and we had a disconnection, likely hibernation scenario
    return recentlyBecameVisible && (Date.now() - this.lastConnectionTime) > 30000;
  }

  ngOnDestroy(): void {
    this.stopReconnection();
    this.destroy$.next();
    this.destroy$.complete();

    // Final cleanup of any remaining timeouts
    this.activeTimeouts.forEach(timeoutId => window.clearTimeout(timeoutId));
    this.activeTimeouts.clear();
  }
}
